import PageHeader from "@/components/PageHeader";
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Target, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAN_MANAGE = ["super_admin", "hr", "manager"];

const REVIEW_CYCLES = [
  { value: "Q1 2026", label: "Q1 2026" },
  { value: "Q2 2026", label: "Q2 2026" },
  { value: "Q3 2026", label: "Q3 2026" },
  { value: "Q4 2026", label: "Q4 2026" },
  { value: "Annual 2026", label: "Annual 2026" },
];

export default function Performance() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);

  const [goals, setGoals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    employee_name: "",
    title: "",
    kra: "",
    kpi: "",
    review_cycle: "Q1 2026",
    weight: 25,
    target: "",
    progress: 0,
    status: "open",
  });

  useEffect(() => { api.get("/goals").then((r) => setGoals(r.data)); }, [refresh]);

  useEffect(() => {
    if (open && employees.length === 0 && canManage) {
      api.get("/employees").then((r) => setEmployees(r.data || []));
    }
  }, [open, canManage, employees.length]);

  const reset = () => setForm({
    employee_id: "", employee_name: "", title: "", kra: "", kpi: "",
    review_cycle: "Q1 2026", weight: 25, target: "", progress: 0, status: "open",
  });

  const submit = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.kra) { toast.error("KRA is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/goals", {
        ...form,
        weight: Number(form.weight) || 0,
        progress: Number(form.progress) || 0,
      });
      toast.success("Goal added");
      setOpen(false);
      reset();
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="performance-page">
      <PageHeader
        title="Performance"
        subtitle="Set goals, track KRAs/KPIs, and manage quarterly & annual reviews."
        action={
          canManage ? (
            <Button data-testid="add-goal" onClick={() => setOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <Plus className="h-4 w-4 mr-1.5" /> Add goal
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {goals.map((g) => (
          <Card key={g.id} data-testid={`goal-${g.id}`} className="hr-card p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-[#0F172A]" />
                <div className="font-display font-semibold">{g.title}</div>
              </div>
              <StatusBadge status={g.status} />
            </div>
            <div className="text-xs text-gray-500 mt-1">{g.employee_name} · {g.review_cycle}</div>
            <div className="text-xs text-gray-600 mt-2">
              <span className="text-gray-400 uppercase tracking-wider mr-1">KRA</span>{g.kra}
              <span className="text-gray-400 uppercase tracking-wider mx-1">·</span>
              <span className="text-gray-400 uppercase tracking-wider mr-1">KPI</span>{g.kpi}
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-gray-500">Progress</span>
                <span className="font-mono">{g.progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#1E88E5]" style={{ width: `${g.progress}%` }} />
              </div>
            </div>
          </Card>
        ))}
        {goals.length === 0 && <Card className="hr-card p-10 text-center text-sm text-gray-500">No goals set yet.</Card>}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="add-goal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-[#F26B3A]" /> Add goal</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => {
                const emp = employees.find((e) => e.employee_id === v);
                setForm({ ...form, employee_id: v, employee_name: emp?.name || "" });
              }}>
                <SelectTrigger data-testid="goal-emp-select" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id || e.id} value={e.employee_id || ""} data-testid={`goal-emp-${e.employee_id}`}>
                      {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Goal title</Label>
              <Input data-testid="goal-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Achieve 95% coding accuracy" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">KRA (Key Responsibility Area)</Label>
              <Textarea rows={2} data-testid="goal-kra" className="mt-1.5" value={form.kra} onChange={(e) => setForm({ ...form, kra: e.target.value })} placeholder="e.g. Charts coding quality" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">KPI (measurable)</Label>
              <Input data-testid="goal-kpi" className="mt-1.5" value={form.kpi} onChange={(e) => setForm({ ...form, kpi: e.target.value })} placeholder="e.g. ≥ 95% accuracy on QA audit" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Review cycle</Label>
              <Select value={form.review_cycle} onValueChange={(v) => setForm({ ...form, review_cycle: v })}>
                <SelectTrigger data-testid="goal-cycle-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVIEW_CYCLES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Weight (%)</Label>
              <Input data-testid="goal-weight" type="number" className="mt-1.5" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Target</Label>
              <Input data-testid="goal-target" className="mt-1.5" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="e.g. 95%" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="goal-cancel" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="goal-submit" onClick={submit} disabled={submitting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add goal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
